import type { ProcessingNotification } from "@repo/helpers";
import type { PageChangeData } from "@repo/ui";

import { name, styles } from "@repo/helpers";
import { Column, GridPreLoaded, useAlertContext } from "@repo/ui";
import { useCallback, useEffect, useMemo, useState } from "react";

import type { Rep } from "../../models";

import { useRepApi } from "../../api/repApi";
import { useRepContext } from "../../contexts/editableItemContext";
import { useSettingsQuery } from "../../helpers/queryHooks";
import style from "./style.module.scss";

const pageSize = 5;

export default function DuplicateReps(props: Props) {
	const { rep, duplicates, setViewedAllDups } = props;

	const getColumn = useCallback(
		(name: string, propertyName: keyof Rep, formatFn?: (r: Rep) => string) => {
			const column = new Column<Rep>(name, propertyName).withFullObject();
			return boldSameValue(column);

			function boldSameValue(column: Column<Rep>) {
				const { propertyName } = column;
				const getProp = (r: Rep) => r[propertyName] as string;
				const getPropUpper = (r: Rep) => getProp(r)?.toUpperCase();
				return column.withFormat((r) => {
					const p1 = getPropUpper(r);
					const p2 = getPropUpper(rep);
					const className = styles(p1 && p1 === p2 && style.duplicateColumn);
					const innards = formatFn ? formatFn(r) : getProp(r);
					return <span className={className}>{innards}</span>;
				});
			}
		},
		[rep],
	);

	const columns = useMemo(
		() =>
			[
				getColumn("Login", "login"),
				new Column<Rep>("State", "state"),
				getColumn("Name", "firstName", (r) => name(r.firstName, r.lastName)),
				getColumn("Email", "email"),
				getColumn("Phone", "phone").withMinWidth(80),
				getColumn("Last Four SSN", "lastFourSSN"),
			] as Column<Rep>[],
		[getColumn],
	);

	const onPageChanged = useCallback(
		({ currentPage, totalPages }: PageChangeData) => {
			if (currentPage === totalPages) setViewedAllDups(true);
		},
		[setViewedAllDups],
	);

	return (
		<>
			<h4 className={style.warning}>Please review carefully, as this Agent may already exist</h4>
			<div className="p-2 text-left">
				<GridPreLoaded
					columns={columns}
					values={duplicates}
					pageSize={pageSize}
					canSetItemsPerPage={false}
					keyProperty="id"
					onPageChanged={onPageChanged}
				/>
			</div>
		</>
	);
}

export function useDuplicateCheck() {
	const { rep } = useRepContext();
	const repApi = useRepApi();
	const { alert, updateAlert, clearAlert } = useAlertContext();
	const [settings] = useSettingsQuery();
	const [viewedAllDups, setViewedAllDups] = useState<boolean | null>(null);
	const requireViewAll = settings?.personalInfoSettings?.requireViewAllPotentialDuplicates ?? false;

	useEffect(() => {
		if (viewedAllDups != null) {
			updateAlert({
				...alert,
				confirmDisabled: !viewedAllDups,
			});
		}
	}, [viewedAllDups]);

	/**
	 * Returns true if save continues
	 */
	const checkForDuplicates = useCallback(
		async (notify: ProcessingNotification) => {
			notify.processing("Checking for Duplicates...");
			const duplicates = await repApi.checkForDuplicates(rep);
			if (duplicates.length) {
				// The grid opens on page 1 and only reports page changes, so a single page counts as viewed
				const viewedAll = !requireViewAll || duplicates.length <= pageSize;
				setViewedAllDups(viewedAll);
				const promise = new Promise<boolean>((resolve) => {
					notify.processing("Duplicates found...");
					updateAlert({
						titleText: "Potential Duplicates Found",
						body: <DuplicateReps rep={rep} duplicates={duplicates} setViewedAllDups={setViewedAllDups} />,
						onConfirm: () => {
							clearAlert();
							resolve(true);
						},
						confirmText: "Continue",
						onCancel: () => {
							clearAlert();
							resolve(false);
						},
						cancelText: "Cancel",
						type: "warning",
						size: "auto",
						confirmDisabled: !viewedAll,
					});
				});
				const result = await promise;
				if (result) notify.processing("Duplicates Bypassed...");
				else notify.info("Stopped");
				return result;
			}
			notify.processing("No Duplicates found, Continuing...");
			return true;
		},
		[clearAlert, rep, repApi, updateAlert, requireViewAll],
	);
	return { checkForDuplicates };
}

interface Props {
	duplicates: Rep[];
	rep: Rep;
	setViewedAllDups: (value: boolean) => void;
}
